/**
 * 智能剪辑（VIDEO_COMPOSE）剪辑数据模型
 * 依据《智能剪辑节点开发方案》v1.4 §3.3 定稿。
 *
 * 版本演进锚点：
 * - v0.3：EditDocument / Track / Clip / TextClip / TextStyle
 * - v0.7：AudioClip（声音面板 §4.6.4）+ TextClip.highlight / animation
 * - v0.8：Clip.lut（滤镜 §4.6.5，P2 启用）
 * - v1.4：转场类型扩充锚点（P2 WebGL 化时启用非淡变系预设）
 */

/** 转场类型：id 与 FFmpeg xfade transition 值一一对应（零翻译成本） */
export type TransitionType =
  // P1 淡变系（CSS opacity 可完美近似预览）
  | 'fade'
  | 'fadeblack'
  | 'fadewhite'
  // P2 首批扩充（v1.4 定锚：随预览 WebGL 化上线，非淡变系）
  | 'dissolve'
  | 'wipeleft'
  | 'wiperight'
  | 'circleopen'
  | 'wipetl'
  | 'slideup'
  | 'squeezeh';

export interface TransitionPreset {
  id: TransitionType;
  name: string;
  category: '淡变' | '擦除' | '滑动';
  /** P1 可用（淡变系 CSS 预览）/ P2（需 WebGL shader 预览） */
  phase: 'p1' | 'p2';
}

/** 转场预设（§4.6.1 v1.3/v1.4 定稿） */
export const TRANSITION_PRESETS: TransitionPreset[] = [
  { id: 'fade', name: '没入淡出', category: '淡变', phase: 'p1' },
  { id: 'fadeblack', name: '黑色过渡', category: '淡变', phase: 'p1' },
  { id: 'fadewhite', name: '白场过渡', category: '淡变', phase: 'p1' },
  // —— P2 首批扩充清单（v1.4 定锚，~10 款，P3 前就位）——
  { id: 'dissolve', name: '像素溶解', category: '淡变', phase: 'p2' },
  { id: 'wipeleft', name: '向左擦除', category: '擦除', phase: 'p2' },
  { id: 'wiperight', name: '向右擦除', category: '擦除', phase: 'p2' },
  { id: 'circleopen', name: '圆形展开', category: '擦除', phase: 'p2' },
  { id: 'wipetl', name: '对角擦除', category: '擦除', phase: 'p2' },
  { id: 'slideup', name: '向上滑动', category: '滑动', phase: 'p2' },
  { id: 'squeezeh', name: '横向挤压', category: '滑动', phase: 'p2' },
];

export type CanvasRatio = '16:9' | '9:16' | '1:1';
export type CanvasResolution = '720P' | '1080P';

/** 剪辑模式（图三五项，§4.1.3；宣传片/预告片为 P2 体裁候选，不上菜单 §4.9 v1.2） */
export type EditMode = 'default' | 'explain' | 'ads' | 'talking' | 'remix';

export const EDIT_MODES: { id: EditMode; name: string }[] = [
  { id: 'default', name: '默认模式' },
  { id: 'explain', name: '讲解视频' },
  { id: 'ads', name: '批量广告' },
  { id: 'talking', name: '口播视频' },
  { id: 'remix', name: '素材混剪' },
];

/** 规格推导（§4.1.3：比例 16:9 / 9:16 / 1:1 × 分辨率 720P / 1080P；时长不限 ✅ §七-7） */
export function specToCanvas(
  ratio: CanvasRatio,
  resolution: CanvasResolution
): { width: number; height: number } {
  const base = resolution === '1080P' ? 1080 : 720;
  switch (ratio) {
    case '16:9':
      return { width: Math.round((base * 16) / 9 / 2) * 2, height: base };
    case '9:16':
      return { width: base, height: Math.round((base * 16) / 9 / 2) * 2 };
    case '1:1':
    default:
      return { width: base, height: base };
  }
}

/** 剪辑工程文档（版本化 JSON，存 node.data.editDocument） */
export interface EditDocument {
  version: 1;
  canvas: {
    ratio: CanvasRatio;
    width: number;
    height: number;
    fps: 30;
    /** 时长不设上限（✅ 已拍板 §七-7）；如未来引入软上限再扩展本字段 */
    maxDurationSec?: number;
  };
  tracks: Track[];
}

export type TrackKind = 'video' | 'text' | 'audio';

export interface Track {
  id: string;
  kind: TrackKind;
  /** text 轨细分：唯一字幕轨 / 独立文字轨（§4.6.2 / §4.6.3） */
  role?: 'subtitle' | 'text';
  name: string;
  /** 图四轨道左侧眼睛 */
  visible: boolean;
  /** 图四轨道左侧喇叭 */
  muted: boolean;
  /** video 轨存 Clip；text 轨存 TextClip；audio 轨存 AudioClip */
  clips: (Clip | TextClip | AudioClip)[];
}

/** 视频片段：素材引用 + trim 起止区间（自动分割 = 同素材多个 trim 实例） */
export interface Clip {
  id: string;
  /** /assets/... 永久 URL（禁存平台临时 URL/base64） */
  src: string;
  /** 源入点（秒） */
  sourceIn: number;
  /** 源出点（秒） */
  sourceOut: number;
  /** 源素材总时长（秒）：右缘修剪扩展的硬上限（不能拖出素材真实长度）；缺省 = 旧文档不限（打开面板时自动回填） */
  sourceDuration?: number;
  /** 时间轴起点（秒）；序列式布局下由顺序推导，显式冗余存储 */
  start: number;
  /** 变速倍数（图五 1.00x），连续滑杆 0.5~2.0（✅ §七-5，atempo 链约束） */
  speed: number;
  /** 音量增益 dB（图五 0.0 dB），-∞(静音)~+10 */
  volumeGainDb: number;
  /** 滤镜/LUT 资产 ID（✅ v0.8：内置滤镜库 P2；缺省 = 无） */
  lut?: string;
  /** 转场：作用于本片段→下一片段之间；末片段 transitionOut 不生效 */
  transitionOut?: { type: TransitionType; durationSec: number };
  /** 配对关联标识（达芬奇「关联素材」）：同 linkId 的视频/音频片段拖动时水平联动移动；缺省 = 独立移动 */
  linkId?: string;
}

/** 文字动画（✅ v0.7 定稿：淡变系，§4.6.3） */
export interface TextAnimation {
  in: 'none' | 'fade';
  out: 'none' | 'fade';
  durationSec: number;
}

/** 关键词高亮（✅ v0.7 定稿：仅颜色着色不加粗，§4.6.2） */
export interface TextHighlight {
  keywords: string[];
  color: string;
}

/** 字幕 / 文字片段 */
export interface TextClip {
  id: string;
  /** 字幕（图七）vs 文字（图十） */
  kind: 'subtitle' | 'text';
  content: string;
  start: number;
  end: number;
  /** 展开后的样式（渲染/导出唯一事实源） */
  style: TextStyle;
  /** 引用的预设样式（图八） */
  presetId?: string;
  /** AI 幂等标记：'ai' = 自动字幕产物（重复执行整体替换，§4.9）；缺省视同 'manual' */
  source?: 'ai' | 'manual';
  highlight?: TextHighlight;
  animation?: TextAnimation;
}

export interface TextStyle {
  /** X（图七 50%），百分比相对画布 */
  xPercent: number;
  /** Y（图七 88%） */
  yPercent: number;
  /** 最大宽度（图七 88%） */
  maxWidthPercent: number;
  /** 缩放（图七 100%） */
  scalePercent: number;
  fontFamily: string;
  /** 相对画布高度的字号线 */
  fontSizeEm: number;
  fontColor: string;
  bold: boolean;
  italic: boolean;
  align: 'left' | 'center' | 'right';
  strokeColor: string;
  strokeWidthEm: number;
  /** 背景框（缺省 = 无背景） */
  bgColor?: string;
  bgOpacity?: number;
}

/** 音频片段（✅ v0.7 定稿 §4.6.4：BGM / 上传音乐 / 纯音频素材共用） */
export interface AudioClip {
  id: string;
  /** /assets/ 永久 URL（内置曲库首次「使用」即转存本项目 assets） */
  src: string;
  /** 曲名（曲库卡片名 / 上传文件名） */
  name: string;
  sourceIn: number;
  sourceOut: number;
  /** 源素材总时长（秒）：非循环音频右缘修剪扩展的硬上限（loop 循环铺满不受限）；缺省 = 旧文档不限 */
  sourceDuration?: number;
  /** 时间轴起点（秒）；添加时默认 0s */
  start: number;
  /** 默认 -6dB（BGM 压底防盖人声） */
  volumeGainDb: number;
  /** 循环铺满文档总时长（默认开） */
  loop: boolean;
  /** 0~5s，默认 1 */
  fadeInSec: number;
  /** 0~5s，默认 1 */
  fadeOutSec: number;
  /** 配对关联标识：同 linkId 的视频/音频片段拖动时水平联动移动；缺省 = 独立移动 */
  linkId?: string;
}

/** 输入栏会话上下文（§4.1.3：发送 = 携带上下文打开面板，P0 起定稿不随期变化） */
export interface SmartEditSession {
  prompt?: string;
  mode?: EditMode;
  spec?: { ratio: CanvasRatio; resolution: CanvasResolution };
}

/** 文档级默认样式（图七默认值） */
export function defaultTextStyle(): TextStyle {
  return {
    xPercent: 50,
    yPercent: 88,
    maxWidthPercent: 88,
    scalePercent: 100,
    fontFamily: 'sans-serif',
    fontSizeEm: 0.05,
    fontColor: '#ffffff',
    bold: false,
    italic: false,
    align: 'center',
    strokeColor: '#000000',
    strokeWidthEm: 0.006,
  };
}

/** 生成一个空剪辑工程文档（§4.3 装载规则 2：按上游素材顺序铺视频轨 V1） */
export function createEmptyDocument(spec: {
  ratio: CanvasRatio;
  resolution: CanvasResolution;
}): EditDocument {
  const { width, height } = specToCanvas(spec.ratio, spec.resolution);
  return {
    version: 1,
    canvas: { ratio: spec.ratio, width, height, fps: 30 },
    tracks: [
      { id: 'track-video', kind: 'video', name: '视频轨 1', visible: true, muted: false, clips: [] },
    ],
  };
}

/** 数值字段防御读取：非有限数值时回退默认值 */
function numField(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/** 布尔字段防御读取：非布尔时回退默认值 */
function boolField(v: unknown, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

/**
 * clip 层字段级归一：旧版持久化文档可能缺失 speed / volumeGainDb / loop 等后加字段，
 * 而 isVideoClip / isAudioClip 守卫以「speed !== undefined / loop !== undefined」区分类型——
 * 缺字段会被误判为未知类型，时长不计入 documentDuration → 总时长归零
 * （时间线标尺塌缩错位、播放按钮立即失效、预览黑屏）。此处按轨型补齐默认值；
 * 无法修复的坏 clip（缺 src / 时长区间非法）直接丢弃，避免 8px 幽灵块。
 */
function normalizeClip(c: unknown, kind: TrackKind): Clip | TextClip | AudioClip | null {
  if (!c || typeof c !== 'object') return null;
  const r = c as Record<string, unknown>;
  if (typeof r.id !== 'string') return null;
  if (kind === 'text') {
    // TextClip 无 src 字段，content 即内容源
    if (typeof r.content !== 'string') return null;
    const start = numField(r.start, 0);
    const end = numField(r.end, start + 3);
    if (end <= start) return null;
    const style =
      r.style && typeof r.style === 'object'
        ? { ...defaultTextStyle(), ...(r.style as Partial<TextStyle>) }
        : defaultTextStyle();
    return { ...(r as unknown as TextClip), start, end, style };
  }
  // Clip / AudioClip 必须有素材引用
  if (typeof r.src !== 'string') return null;
  const sourceIn = numField(r.sourceIn, 0);
  const sourceOut = numField(r.sourceOut, Number.NaN);
  if (!Number.isFinite(sourceOut) || sourceOut <= sourceIn) return null;
  if (kind === 'video') {
    return {
      ...(r as unknown as Clip),
      start: numField(r.start, 0),
      sourceIn,
      sourceOut,
      speed: numField(r.speed, 1),
      // -∞（配对音频静音标记）JSON 序列化为 null：读回时还原 -∞ 而非回退 0dB，
      // 否则保存重开后视频取消静音 → 与配对音频双重声音
      volumeGainDb: r.volumeGainDb === null ? Number.NEGATIVE_INFINITY : numField(r.volumeGainDb, 0),
      // linkId 脏数据防御：非字符串一律视为未关联
      linkId: typeof r.linkId === 'string' ? r.linkId : undefined,
    };
  }
  return {
    ...(r as unknown as AudioClip),
    name: typeof r.name === 'string' ? r.name : '音频',
    start: numField(r.start, 0),
    sourceIn,
    sourceOut,
    volumeGainDb: numField(r.volumeGainDb, -6),
    loop: boolField(r.loop, true),
    fadeInSec: numField(r.fadeInSec, 0),
    fadeOutSec: numField(r.fadeOutSec, 0),
    linkId: typeof r.linkId === 'string' ? r.linkId : undefined,
  };
}

function normalizeTrack(raw: unknown, index: number): Track {
  const t = (raw ?? {}) as Record<string, unknown>;
  const kind: TrackKind = t.kind === 'video' || t.kind === 'text' || t.kind === 'audio' ? t.kind : 'video';
  const clips = (Array.isArray(t.clips) ? t.clips : [])
    .map((c) => normalizeClip(c, kind))
    .filter((c): c is Clip | TextClip | AudioClip => c !== null);
  return {
    id: typeof t.id === 'string' ? t.id : `track-${kind}-${index}`,
    kind,
    role: t.role === 'subtitle' || t.role === 'text' ? t.role : undefined,
    name: typeof t.name === 'string' && t.name ? t.name : kind === 'video' ? '视频轨' : kind === 'audio' ? '音频轨' : '文字轨',
    visible: boolField(t.visible, true),
    muted: boolField(t.muted, false),
    clips,
  };
}

/** 未知字段容忍的向前兼容读取防御（§六-4，脚本节点 dataVersion 先例）；
 *  含 clip 层字段级归一（normalizeClip），保证守卫函数与时长换算总能拿到完整字段 */
export function normalizeEditDocument(raw: unknown): EditDocument | null {
  if (!raw || typeof raw !== 'object') return null;
  const doc = raw as Partial<EditDocument> & { version?: number };
  if (doc.version !== 1 || !doc.canvas || !Array.isArray(doc.tracks)) return null;
  const { ratio = '16:9', width = 1280, height = 720, fps = 30 } = doc.canvas;
  return {
    version: 1,
    canvas: { ratio, width, height, fps: 30 },
    tracks: doc.tracks.map((t, i) => normalizeTrack(t, i)),
  };
}
